// src/components/EquipmentManager/AddEquipmentModal.js

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { auth } from '../../firebaseConfig';
import styles from './AddEquipmentModal.module.css';
import {
  FaTimes,
  FaBox,
  FaTag,
  FaInfoCircle,
  FaImage,
  FaLink,
  FaDollarSign,
  FaStar,
  FaHashtag,
  FaCheckCircle,
  FaTools,
  FaStickyNote,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaEdit
} from 'react-icons/fa';

const AddEquipmentModal = ({
  isOpen,
  onClose,
  onEquipmentAdded,
  sportName,
  equipmentToEdit,
  programId,
}) => {
  const isEditing = Boolean(equipmentToEdit);
  const [clubId, setClubId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Initialize state for all form fields
  const [formData, setFormData] = useState({
    equipmentName: '',
    description: '',
    imageUrl: '',
    amazonUrl: '', // Renamed from buyUrl, Amazon only
    category: '', // Will be categoryId (ObjectId)
    averagePrice: '',
    skillLevel: 'Beginner',
    maintenanceInfo: '',
    quantity: 1,
    condition: 'good',
    sponsorName: '', // NEW - for sponsor store
    sponsorLogo: '', // NEW - for branding
    tags: '' // NEW - for better search
  });

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch program to get clubId
  useEffect(() => {
    const fetchProgramData = async () => {
      if (!programId) {
        console.warn('AddEquipmentModal: No programId provided');
        return;
      }
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch program: ${response.status}`);
        }
        
        const program = await response.json();
        
        // Extract clubId
        const extractedClubId = program.clubId && typeof program.clubId === 'object'
          ? program.clubId._id
          : program.clubId;
        
        setClubId(extractedClubId);
      } catch (err) {
        console.error('AddEquipmentModal: Error fetching program data:', err);
      }
    };
    
    if (isOpen) {
      fetchProgramData();
    }
  }, [programId, isOpen]);
  
  // Fetch categories from backend
  useEffect(() => {
    const fetchCategories = async () => {
      if (!clubId) return;
      
      try {
        setLoadingCategories(true);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/equipment-categories?clubId=${clubId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          const categoriesArray = data.categories || data || [];
          setCategories(categoriesArray);
        } else {
          console.warn('Categories endpoint not available, using fallback');
          // Fallback to default categories
          setCategories([
            { _id: 'ball', name: 'Balls & Pucks', icon: '⚽' },
            { _id: 'footwear', name: 'Footwear', icon: '👟' },
            { _id: 'protective', name: 'Protective Gear', icon: '🛡️' },
            { _id: 'apparel', name: 'Apparel', icon: '👕' },
            { _id: 'training', name: 'Training Equipment', icon: '🏋️' },
            { _id: 'field', name: 'Field Equipment', icon: '🥅' },
            { _id: 'bags', name: 'Bags & Storage', icon: '🎒' },
            { _id: 'accessories', name: 'Accessories', icon: '🔧' },
            { _id: 'other', name: 'Other', icon: '📦' }
          ]);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        // Use fallback categories
        setCategories([
          { _id: 'ball', name: 'Balls & Pucks', icon: '⚽' },
          { _id: 'footwear', name: 'Footwear', icon: '👟' },
          { _id: 'protective', name: 'Protective Gear', icon: '🛡️' },
          { _id: 'apparel', name: 'Apparel', icon: '👕' },
          { _id: 'other', name: 'Other', icon: '📦' }
        ]);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    if (isOpen && clubId) {
      fetchCategories();
    }
  }, [clubId, isOpen]);

  // Populate fields if editing
  useEffect(() => {
    if (isEditing && equipmentToEdit) {
      // Extract image URL from new or old format
      const imageUrl = equipmentToEdit.images?.[0]?.url || 
                       equipmentToEdit.imageUrl || '';
      
      // Extract Amazon URL from new or old format
      const amazonUrl = equipmentToEdit.purchaseLinks?.amazon || 
                        equipmentToEdit.purchaseLinks?.directStore || 
                        equipmentToEdit.buyUrl || '';
      
      // Extract sponsor info
      const sponsorName = equipmentToEdit.sponsorInfo?.name || '';
      const sponsorLogo = equipmentToEdit.sponsorInfo?.logo || '';
      
      // Extract tags
      const tags = Array.isArray(equipmentToEdit.tags) ? equipmentToEdit.tags.join(', ') : '';
      
      setFormData({
        equipmentName: equipmentToEdit.equipmentName || '',
        description: equipmentToEdit.description || '',
        imageUrl: imageUrl,
        amazonUrl: amazonUrl,
        // Handle both old (string) and new (ObjectId) category formats
        category: equipmentToEdit.category?._id || equipmentToEdit.category || '',
        averagePrice: equipmentToEdit.averagePrice || '',
        skillLevel: equipmentToEdit.skillLevel || 'Beginner',
        maintenanceInfo: equipmentToEdit.maintenanceInfo || '',
        quantity: equipmentToEdit.quantity || 1,
        condition: equipmentToEdit.condition || 'good',
        sponsorName: sponsorName,
        sponsorLogo: sponsorLogo,
        tags: tags
      });
    } else {
      // Reset form fields when adding new equipment
      setFormData({
        equipmentName: '',
        description: '',
        imageUrl: '',
        amazonUrl: '',
        category: '', // Will be set from dropdown
        averagePrice: '',
        skillLevel: 'Beginner',
        maintenanceInfo: '',
        quantity: 1,
        condition: 'good',
        sponsorName: '',
        sponsorLogo: '',
        tags: ''
      });
    }
  }, [isEditing, equipmentToEdit, isOpen]);

  if (!isOpen) return null;

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested dimensions fields
    if (name.startsWith('dimensions.')) {
      const dimensionField = name.split('.')[1];
      setFormData((prevData) => ({
        ...prevData,
        dimensions: {
          ...prevData.dimensions,
          [dimensionField]: value,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleAddOrEditEquipment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Client-side validation
    if (!formData.equipmentName.trim()) {
      setError('Equipment name is required');
      setLoading(false);
      return;
    }

    if (!formData.category) {
      setError('Category is required');
      setLoading(false);
      return;
    }

    if (!formData.skillLevel) {
      setError('Skill level is required');
      setLoading(false);
      return;
    }

    if (!formData.condition) {
      setError('Condition is required');
      setLoading(false);
      return;
    }

    if (!sportName) {
      setError('Sport name is required');
      setLoading(false);
      return;
    }
    
    if (!clubId) {
      setError('Club information not loaded. Please wait or refresh the page.');
      setLoading(false);
      return;
    }
    
    // Amazon URL validation (security)
    if (formData.amazonUrl.trim()) {
      const amazonUrl = formData.amazonUrl.trim().toLowerCase();
      const isAmazonUrl = amazonUrl.includes('amazon.com') || 
                          amazonUrl.includes('amzn.to') ||
                          amazonUrl.includes('amazon.co.uk') ||
                          amazonUrl.includes('amazon.ca') ||
                          amazonUrl.includes('amazon.de') ||
                          amazonUrl.includes('amazon.fr') ||
                          amazonUrl.includes('amazon.in') ||
                          amazonUrl.includes('amazon.it') ||
                          amazonUrl.includes('amazon.es') ||
                          amazonUrl.includes('amazon.com.au') ||
                          amazonUrl.includes('amazon.com.mx') ||
                          amazonUrl.includes('amazon.com.br') ||
                          amazonUrl.includes('amazon.co.jp');
      
      if (!isAmazonUrl) {
        setError('Only Amazon product links are allowed for security reasons');
        setLoading(false);
        return;
      }
    }

    try {
      // Prepare the equipment data with new backend format
      const equipmentData = {
        sportName: sportName, // Required field in backend schema
        equipmentName: formData.equipmentName.trim(),
        description: formData.description.trim(),
        category: formData.category,
        averagePrice: formData.averagePrice ? parseFloat(formData.averagePrice) : undefined,
        skillLevel: formData.skillLevel,
        maintenanceInfo: formData.maintenanceInfo.trim(),
        quantity: parseInt(formData.quantity) || 1,
        condition: formData.condition,
        
        // Sponsor information
        ...(formData.sponsorName.trim() && {
          sponsorInfo: {
            name: formData.sponsorName.trim(),
            ...(formData.sponsorLogo.trim() && { logo: formData.sponsorLogo.trim() })
          }
        }),
        
        // Tags for better search
        ...(formData.tags.trim() && {
          tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        }),
        
        // New backend format: images array instead of single imageUrl
        images: formData.imageUrl.trim() ? [{
          url: formData.imageUrl.trim(),
          isPrimary: true,
          alt: formData.equipmentName.trim(),
          order: 0
        }] : [],
        
        // New backend format: purchaseLinks object (Amazon only for security)
        purchaseLinks: {
          ...(formData.amazonUrl.trim() && { amazon: formData.amazonUrl.trim() })
        },
        
        // New backend format: scope (program level)
        scope: {
          level: 'program',
          clubId: clubId,
          programId: programId
        }
      };

      // Remove undefined values
      Object.keys(equipmentData).forEach(key => {
        if (equipmentData[key] === undefined || equipmentData[key] === '') {
          delete equipmentData[key];
        }
      });

      const url = isEditing
        ? `${process.env.REACT_APP_BACKEND_URL}/api/sports-equipments/${equipmentToEdit._id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/sports-equipments`;

      const method = isEditing ? 'PUT' : 'POST';

      // Get Firebase auth token
      const headers = {
        'Content-Type': 'application/json'
      };
      
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const token = await currentUser.getIdToken();
          headers.Authorization = `Bearer ${token}`;
        }
      } catch (authError) {
        console.error('Error getting auth token:', authError);
      }

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(equipmentData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        let errorMessage = 'Failed to add/edit equipment';
        
        // Extract error message from various backend formats
        if (errorData.error) {
          // Check if error is an object with message or code properties
          if (typeof errorData.error === 'object') {
            errorMessage = errorData.error.message || errorData.error.code || JSON.stringify(errorData.error);
          } else {
            errorMessage = errorData.error;
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          // Handle validation errors array
          errorMessage = errorData.errors.map(err => err.msg || err.message).join(', ');
        } else if (errorData.details && Array.isArray(errorData.details)) {
          // Handle Joi validation errors
          errorMessage = errorData.details.map(detail => detail.message).join(', ');
        } else if (response.status === 400) {
          errorMessage = 'Invalid data provided. Please check your input.';
        } else if (response.status === 401) {
          errorMessage = 'Unauthorized. Please log in again.';
        } else if (response.status === 403) {
          errorMessage = 'Forbidden. You do not have permission to perform this action.';
        } else if (response.status === 404) {
          errorMessage = 'Equipment not found.';
        } else if (response.status === 409) {
          errorMessage = 'Equipment already exists.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      onEquipmentAdded(result);
      onClose();
    } catch (error) {
      console.error('Error adding/editing equipment:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            {isEditing ? <FaEdit className={styles.modalTitleIcon} /> : <FaBox className={styles.modalTitleIcon} />}
            <h3>{isEditing ? 'Edit Equipment' : 'Add New Equipment'}</h3>
          </div>
          <div className={styles.headerControls}>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close Modal">
              <FaTimes />
            </button>
          </div>
        </div>

        <div className={styles.modalBody}>
          {error && (
            <div className={styles.errorMessage}>
              <FaExclamationTriangle className={styles.errorIcon} />
              {error}
            </div>
          )}

          <form onSubmit={handleAddOrEditEquipment} className={styles.form}>
            {/* Equipment Name */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="equipmentName" className={styles.formLabel}>
                  <FaBox className={styles.formLabelIcon} />
                  Equipment Name *
                </label>
                <input
                  type="text"
                  id="equipmentName"
                  name="equipmentName"
                  value={formData.equipmentName}
                  onChange={handleChange}
                  className={styles.formInput}
                  required
                  placeholder="Enter equipment name"
                />
              </div>

              {/* Category */}
              <div className={styles.formGroup}>
                <label htmlFor="category" className={styles.formLabel}>
                  <FaTag className={styles.formLabelIcon} />
                  Category *
                </label>
                {loadingCategories ? (
                  <div className={styles.formInput} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <span style={{fontSize: '12px'}}>Loading categories...</span>
                  </div>
                ) : (
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={styles.formSelect}
                    required
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Description */}
            <div className={styles.formGroupFull}>
              <label htmlFor="description" className={styles.formLabel}>
                <FaInfoCircle className={styles.formLabelIcon} />
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={styles.formTextarea}
                placeholder="Describe the equipment..."
                rows="3"
              ></textarea>
            </div>

            {/* Image URL and Amazon Product Link */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="imageUrl" className={styles.formLabel}>
                  <FaImage className={styles.formLabelIcon} />
                  Product Image URL
                </label>
                <input
                  type="url"
                  id="imageUrl"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  className={styles.formInput}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="amazonUrl" className={styles.formLabel}>
                  <FaLink className={styles.formLabelIcon} />
                  Amazon Product Link
                </label>
                <input
                  type="url"
                  id="amazonUrl"
                  name="amazonUrl"
                  value={formData.amazonUrl}
                  onChange={handleChange}
                  className={styles.formInput}
                  placeholder="https://amazon.com/product-link"
                />
                <small style={{fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: '4px'}}>
                  Only Amazon URLs allowed for security
                </small>
              </div>
            </div>
            
            {/* Sponsor Information */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="sponsorName" className={styles.formLabel}>
                  <FaBox className={styles.formLabelIcon} />
                  Sponsor Name
                </label>
                <input
                  type="text"
                  id="sponsorName"
                  name="sponsorName"
                  value={formData.sponsorName}
                  onChange={handleChange}
                  className={styles.formInput}
                  placeholder="e.g., Nike, Adidas"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="sponsorLogo" className={styles.formLabel}>
                  <FaImage className={styles.formLabelIcon} />
                  Sponsor Logo URL
                </label>
                <input
                  type="url"
                  id="sponsorLogo"
                  name="sponsorLogo"
                  value={formData.sponsorLogo}
                  onChange={handleChange}
                  className={styles.formInput}
                  placeholder="https://example.com/logo.png"
                />
              </div>
            </div>

            {/* Average Price and Skill Level */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="averagePrice" className={styles.formLabel}>
                  <FaDollarSign className={styles.formLabelIcon} />
                  Average Price ($)
                </label>
                <input
                  type="number"
                  id="averagePrice"
                  name="averagePrice"
                  value={formData.averagePrice}
                  onChange={handleChange}
                  className={styles.formInput}
                  min="0"
                  step="0.01"
                  placeholder="e.g., 49.99"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="skillLevel" className={styles.formLabel}>
                  <FaStar className={styles.formLabelIcon} />
                  Skill Level
                </label>
                <select
                  id="skillLevel"
                  name="skillLevel"
                  value={formData.skillLevel}
                  onChange={handleChange}
                  className={styles.formSelect}
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            </div>

            {/* Quantity and Condition */}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="quantity" className={styles.formLabel}>
                  <FaHashtag className={styles.formLabelIcon} />
                  Quantity *
                </label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  className={styles.formInput}
                  min="1"
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="condition" className={styles.formLabel}>
                  <FaCheckCircle className={styles.formLabelIcon} />
                  Condition *
                </label>
                <select
                  id="condition"
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  className={styles.formSelect}
                  required
                >
                  <option value="new">New</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                </select>
              </div>
            </div>

            {/* Tags */}
            <div className={styles.formGroupFull}>
              <label htmlFor="tags" className={styles.formLabel}>
                <FaTag className={styles.formLabelIcon} />
                Tags
              </label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                className={styles.formInput}
                placeholder="Comma-separated (e.g., youth, indoor, waterproof, professional)"
              />
              <small style={{fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: '4px'}}>
                Tags help users find equipment through search
              </small>
            </div>
            
            {/* Care Instructions (renamed from Maintenance Info) */}
            <div className={styles.formGroupFull}>
              <label htmlFor="maintenanceInfo" className={styles.formLabel}>
                <FaTools className={styles.formLabelIcon} />
                Care Instructions (Optional)
              </label>
              <textarea
                id="maintenanceInfo"
                name="maintenanceInfo"
                value={formData.maintenanceInfo}
                onChange={handleChange}
                className={styles.formTextarea}
                placeholder="How to care for and maintain this equipment..."
                rows="3"
              ></textarea>
            </div>
          </form>
        </div>

        <div className={styles.buttonContainer}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleAddOrEditEquipment}
            className={styles.submitButton}
            disabled={loading}
          >
            {loading
              ? isEditing
                ? 'Updating...'
                : 'Adding...'
              : isEditing
              ? 'Update Equipment'
              : 'Add Equipment'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

AddEquipmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEquipmentAdded: PropTypes.func.isRequired,
  sportName: PropTypes.string.isRequired, // Required since backend needs it
  equipmentToEdit: PropTypes.object, // Optional for editing
};

export default AddEquipmentModal;
